import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Typography,
} from '@growdash/design-system/atoms';
import { GROWINTEL_PATHS } from '@growdash/growintel/constants/GrowintelPaths';
import { useRouter } from '@growdash/growintel/hooks/useRouter';

import { DataTable } from '@growdash/design-system/molecules';

import PageFooter from '@growdash/growintel/components/molecules/page-footer';
import SelectBrand from '@growdash/growintel/components/molecules/select-brand';
import useBrands from '@growdash/growintel/hooks/useBrands';
import useCurrency from '@growdash/growintel/hooks/useCurrency';
import useTabState from '@growdash/growintel/hooks/useTabState';
import { Archive, Loader2 } from 'lucide-react';
import React, { useCallback } from 'react';
import PageWithTitle from '../../components/hocs/with-title';
import { useGrowintelService } from '../../hooks/useGrowintelService';
import useWatchlist from '../../hooks/useWatchlist';
import AishaWatchlist from './components/aisha-insights';
import LocationInsights from './components/location-insights';
import WatchlistOnboardingDialog from './components/onboarding-dialog';
import columns from './table-columns';

type Props = {};

export default function WatchListView({}: Props) {
  const { watchlist } = useWatchlist();
  const userBrands = useBrands();
  const service = useGrowintelService();
  const currency = useCurrency();

  const [activeRemoveId, setActiveRemoveId] = React.useState<string | null>();
  const [removeBrandLoading, setRemoveBrandLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);

  const { filters, updateTabState } = useTabState('watchlist');

  const {
    watchlist: data,
    refetchWatchlist,
    isWatchlistLoading,
  } = useWatchlist();

  const { updateTabState: updateSearchPageState } = useTabState('search');
  const router = useRouter();

  const removeFromWatchlist = useCallback(async () => {
    if (!activeRemoveId) return;
    try {
      setRemoveBrandLoading(true);
      await service?.watchlistAPI.removeBrandFromWatchlistWatchlistBrandIdDelete(
        activeRemoveId,
      );
      await refetchWatchlist();
      setActiveRemoveId(null);
      setRemoveBrandLoading(false);
    } catch (error) {
      return;
    } finally {
      setRemoveBrandLoading(false);
    }
  }, [activeRemoveId]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleClearFilters = () => {
    updateTabState({
      filters: {
        ...filters,
        brandId: '',
      },
    });
  };

  const RenderWatchlistTable = () => (
    <Card className="overflow-hidden">
      <CardHeader className="">
        <CardTitle>Watchlist brands</CardTitle>
        {!isWatchlistLoading && (
          <span className="text-xs text-[#72778A] font-medium animate-fade-in">{`${data?.length} restaurants`}</span>
        )}
      </CardHeader>

      {!isWatchlistLoading && !(data?.length ?? -1 > 0) ? (
        <CardContent>
          <div className="w-full text-[#999CA9] flex flex-col items-center py-6 gap-6 animate-fade-in">
            <h2 className="text-2xl font-bold text-[#999CA9]">
              Nothing here - yet!
            </h2>
            <Archive className="w-12 h-12 text-[#E1E1EC]" />
            <h3 className="text-[#999CA9] font-medium">
              The restaurants in your watchlist will appear here.
            </h3>
            <Button
              onClick={() => {
                updateSearchPageState({ selectedSubTab: 'brand' });
                router.replace(GROWINTEL_PATHS.SEARCH);
              }}
              className="bg-primary font-semibold"
            >
              Start building your Watchlist
            </Button>
          </div>
        </CardContent>
      ) : (
        <DataTable
          paginationContainerClassName="p-4"
          columns={columns({
            currency: currency ?? '',
            setActiveRemoveId,
          })}
          totalDataCount={data?.length}
          data={data ?? []}
          isLoading={isWatchlistLoading}
          isPaginationActive
          pagination={{
            pageIndex: page - 1, // DataTable uses 0-based indexing
            pageSize: pageSize,
            pageCount: Math.floor(data?.length ?? 0) / pageSize,
            onPageChange: (newPage) => handlePageChange(newPage + 1), // Convert back to 1-based indexing
            onRowsPerPageChange: handlePageSizeChange,
          }}
        />
      )}
    </Card>
  );
  const RenderRemoveBrandDialog = () => (
    <Dialog
      onOpenChange={(isOpen) => {
        if (!isOpen) setActiveRemoveId(null);
      }}
      open={!!activeRemoveId}
    >
      <DialogContent>
        <DialogTitle>Remove from watchlist</DialogTitle>
        <DialogDescription>
          <p className="min-h-12">
            {' '}
            Are you sure you want to remove{' '}
            <strong>
              {data?.find((b) => b.id === activeRemoveId)?.brand_name ??
                'this brand '}
            </strong>{' '}
            from your watchlist?
          </p>
        </DialogDescription>
        <DialogFooter className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              setActiveRemoveId(null);
            }}
          >
            Cancel
          </Button>
          <Button
            className="shadow-none"
            disabled={removeBrandLoading}
            onClick={removeFromWatchlist}
          >
            {removeBrandLoading ? (
              <Loader2 className="animate-spin" />
            ) : (
              'Remove'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      {!isWatchlistLoading && !watchlist?.length && (
        <WatchlistOnboardingDialog />
      )}
      <PageWithTitle title="Watchlist">
        <Card className="shadow-none">
          <CardHeader className="border-b-secondary">
            <div className="flex gap-4 items-center">
              <Typography className="text-sm  text-neutral font-medium">
                {' '}
                Insights for*
              </Typography>
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipContent
                    hidden={
                      isWatchlistLoading ||
                      !!(watchlist?.length && userBrands.length)
                    }
                  >
                    Add a brand to your watchlist to see detailed insights
                  </TooltipContent>
                  <TooltipTrigger>
                    <SelectBrand
                      className="lg:min-w-48"
                      value={filters.brandId ?? ''}
                      disabled={!watchlist?.length}
                      onValueChange={(brand) => {
                        updateTabState({
                          filters: {
                            ...filters,
                            brandId: brand.id,
                          },
                        });
                      }}
                    />
                  </TooltipTrigger>
                </Tooltip>
              </TooltipProvider>
              {!!filters.brandId && (
                <Button
                  onClick={handleClearFilters}
                  variant="link"
                  className="animate-fade-in text-secondary underline-offset-[5px] underline"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="bg-quarternary p-4">
            <div className="grid animate-fade-in lg:grid-cols-12 gap-4">
              <div className="lg:col-span-4 flex flex-col gap-4">
                <AishaWatchlist
                  brandNameForInsights={
                    userBrands?.find((b) => b.id === filters.brandId)
                      ?.brand_name
                  }
                  watchlistBrands={watchlist ?? []}
                  isEmptyState={!filters?.brandId}
                />
                <LocationInsights />
              </div>
              <div className="lg:col-span-8 flex flex-col gap-4">
                {RenderWatchlistTable()}
              </div>
            </div>
            <PageFooter currentPage="watchlist" />
          </CardContent>
        </Card>
        {RenderRemoveBrandDialog()}
      </PageWithTitle>
    </>
  );
}
